import React from 'react';
import LegalLayout from './LegalLayout';
import { Lock, Shield, Database } from 'lucide-react';

const Article = ({ num, title, children }) => (
  <section className="border-l-4 border-[#205F79]/20 pl-5 py-1">
    <h2 className="text-xl font-bold text-gray-900 mb-3">
      <span className="text-[#205F79]">Article {num}</span> — {title}
    </h2>
    <div className="space-y-3 text-[15px] leading-relaxed">{children}</div>
  </section>
);

const Confidentialite = () => (
  <LegalLayout title="Politique de Confidentialité (RGPD)" lastUpdated="Mai 2026">
    <p className="text-gray-600">
      La présente Politique de Confidentialité décrit la manière dont <strong>DIP PILOTE +IA</strong> (ci-après
      « l'Éditeur ») collecte, utilise et protège les données personnelles des utilisateurs du Service, conformément au
      Règlement (UE) 2016/679 du 27 avril 2016 (« RGPD ») et à la loi Informatique et Libertés modifiée.
    </p>

    {/* Engagements forts */}
    <div className="grid sm:grid-cols-3 gap-3 my-6">
      <div className="border border-green-200 bg-green-50 rounded-lg p-4">
        <Lock className="text-green-700 mb-2" size={22} />
        <p className="font-semibold text-green-900 text-sm mb-1">Confidentialité absolue</p>
        <p className="text-xs text-green-800">Vos formules et recettes restent votre propriété exclusive.</p>
      </div>
      <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
        <Shield className="text-blue-700 mb-2" size={22} />
        <p className="font-semibold text-blue-900 text-sm mb-1">Aucun entraînement IA</p>
        <p className="text-xs text-blue-800">Vos données ne servent jamais à entraîner des modèles d'IA publics.</p>
      </div>
      <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
        <Database className="text-amber-700 mb-2" size={22} />
        <p className="font-semibold text-amber-900 text-sm mb-1">Hébergement UE</p>
        <p className="text-xs text-amber-800">Données stockées sur des serveurs situés au sein de l'Union européenne.</p>
      </div>
    </div>

    <Article num={1} title="Responsable du traitement">
      <p>
        Le responsable du traitement des données personnelles est <strong>DIP PILOTE +IA</strong>, joignable par email à
        l'adresse : <strong>dippilote@outlook.com</strong>.
      </p>
    </Article>

    <Article num={2} title="Données collectées">
      <p>L'Éditeur collecte les catégories de données suivantes :</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Données de compte :</strong> nom, prénom, email, mot de passe (chiffré côté serveur), nom de l'entreprise / laboratoire.</li>
        <li><strong>Données métier saisies par l'Utilisateur :</strong> formules cosmétiques, listes d'ingrédients, recettes de production, fiches matières premières, étiquettes, rapports toxicologiques, certificats fournisseurs.</li>
        <li><strong>Données de paiement :</strong> gérées exclusivement par Stripe. L'Éditeur ne stocke aucune donnée bancaire.</li>
        <li><strong>Données de connexion :</strong> adresse IP, dates et heures de connexion, navigateur, système d'exploitation (à des fins de sécurité et de prévention des fraudes).</li>
        <li><strong>Échanges avec le support :</strong> contenu des messages envoyés via la messagerie SAV intégrée.</li>
        <li><strong>Conversations avec l'IA :</strong> historique des requêtes envoyées à l'assistant pour assurer la mémoire conversationnelle.</li>
      </ul>
    </Article>

    <Article num={3} title="Confidentialité absolue des formules et recettes">
      <div className="bg-green-50 border-2 border-green-300 rounded-lg p-5 my-3">
        <p className="font-semibold text-green-900 mb-2">
          Engagement fort de l'Éditeur :
        </p>
        <ul className="list-disc pl-5 space-y-2 text-green-900">
          <li>
            Les <strong>formules cosmétiques, recettes de production, dosages, procédés de fabrication, savoir-faire et
            tout secret industriel</strong> saisis par l'Utilisateur dans le Service demeurent sa <strong>propriété
            exclusive</strong> et sont protégés par le secret des affaires (articles L.151-1 et suivants du Code de commerce).
          </li>
          <li>
            L'Éditeur s'engage à <strong>ne jamais commercialiser, partager, vendre, louer ou divulguer</strong> à un
            tiers, à quelque fin que ce soit, les formules, recettes, ou données métier d'un Utilisateur, sauf obligation
            légale impérieuse.
          </li>
          <li>
            <strong>Les administrateurs du Service (y compris l'éditeur lui-même) n'ont AUCUN accès en lecture aux
            formules cosmétiques, recettes de production, dosages, ingrédients, fiches matières premières, étiquettes,
            rapports toxicologiques, ni aux DIP générés par les Utilisateurs.</strong> Le tableau d'administration ne permet
            que la gestion administrative des comptes (nom, email, plan, statut, support technique). Les données métier
            sont stockées de manière isolée et ne sont consultables que par l'Utilisateur propriétaire du compte.
          </li>
          <li>
            L'accès technique aux données métier d'un Utilisateur par les équipes de l'Éditeur ne peut intervenir qu'en
            cas de demande explicite de l'Utilisateur (assistance technique, restauration), ou en cas d'obligation
            légale, et est strictement tracé et auditable.
          </li>
        </ul>
      </div>
    </Article>

    <Article num={4} title="Données et assistant Intelligence Artificielle">
      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-5 my-3">
        <p className="font-semibold text-blue-900 mb-2">
          Garantie de non-utilisation pour l'entraînement de modèles publics :
        </p>
        <ul className="list-disc pl-5 space-y-2 text-blue-900">
          <li>
            Les requêtes envoyées à l'assistant IA <strong>ne sont jamais utilisées pour entraîner des modèles d'IA
            publics</strong>, ni revendues à des fournisseurs tiers à des fins d'entraînement.
          </li>
          <li>
            L'assistant IA repose techniquement sur des modèles tiers (notamment OpenAI / Anthropic / Google) accessibles
            via des API. Les fournisseurs d'API utilisés se sont contractuellement engagés à <strong>ne pas conserver
            les données envoyées</strong> via leur API entreprise / Universal Key au-delà de la durée nécessaire à la
            réponse, et à <strong>ne pas les utiliser pour l'entraînement</strong> de leurs modèles publics, conformément
            à leurs propres engagements (politiques « Zero Retention » ou « API Data Usage Policy »).
          </li>
          <li>
            L'historique conversationnel est stocké dans la base de données du Service à <strong>seule fin de fournir
            une mémoire contextuelle</strong> à l'assistant pour l'Utilisateur et peut être supprimé à tout moment depuis
            l'interface ou sur demande.
          </li>
        </ul>
      </div>
    </Article>

    <Article num={5} title="Bases légales du traitement">
      <p>Les traitements sont fondés sur les bases légales suivantes :</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Exécution du contrat</strong> (article 6.1.b RGPD) : pour la fourniture du Service, la gestion du compte, le support utilisateur, le traitement des paiements.</li>
        <li><strong>Intérêt légitime</strong> (article 6.1.f RGPD) : pour la sécurité du Service, la prévention des fraudes, l'amélioration des fonctionnalités.</li>
        <li><strong>Consentement</strong> (article 6.1.a RGPD) : pour les communications marketing facultatives, lorsqu'elles sont mises en place.</li>
        <li><strong>Obligation légale</strong> (article 6.1.c RGPD) : pour la conservation comptable et fiscale des factures.</li>
      </ul>
    </Article>

    <Article num={6} title="Paiements — Stripe">
      <p>
        Les paiements (abonnement Premium mensuel ou annuel) sont opérés exclusivement par <strong>Stripe Payments
        Europe Ltd.</strong> (1 Grand Canal Street Lower, Grand Canal Dock, Dublin, Irlande), prestataire certifié
        <strong> PCI-DSS niveau 1</strong>.
      </p>
      <p>
        L'Éditeur ne collecte ni ne stocke aucune donnée bancaire (numéro de carte, date d'expiration, cryptogramme).
        Les seules données reçues de Stripe et conservées par l'Éditeur sont l'identifiant de session, le statut du
        paiement, le montant, la devise et l'horodatage, à des fins de facturation et de suivi de l'abonnement.
      </p>
      <p>
        La politique de confidentialité de Stripe est consultable à l'adresse :{' '}
        <a href="https://stripe.com/fr/privacy" target="_blank" rel="noopener noreferrer" className="text-[#205F79] underline">
          stripe.com/fr/privacy
        </a>.
      </p>
    </Article>

    <Article num={7} title="Durées de conservation">
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Compte utilisateur</strong> : pendant toute la durée d'usage du Service + 30 jours après suppression du compte.</li>
        <li><strong>Données métier (formules, recettes, DIP)</strong> : tant que le compte existe, supprimées sous 30 jours après suppression du compte.</li>
        <li><strong>Factures et données de paiement</strong> : 10 ans (obligation comptable et fiscale française).</li>
        <li><strong>Logs de connexion</strong> : 12 mois maximum.</li>
        <li><strong>Échanges support</strong> : 3 ans après le dernier contact.</li>
      </ul>
    </Article>

    <Article num={8} title="Hébergement et localisation">
      <p>
        Les données personnelles et métier sont hébergées sur des serveurs situés au sein de <strong>l'Union européenne</strong>.
        En cas de transfert hors UE (par exemple via les API d'IA), l'Éditeur veille à ce que ce transfert soit encadré
        par des <strong>clauses contractuelles types</strong> approuvées par la Commission européenne ou un mécanisme
        équivalent reconnu par la CNIL.
      </p>
    </Article>

    <Article num={9} title="Sécurité">
      <p>
        L'Éditeur met en œuvre les mesures techniques et organisationnelles nécessaires à la protection des données :
        chiffrement des communications (HTTPS/TLS), chiffrement des mots de passe en base, contrôles d'accès, sauvegardes
        régulières, journalisation des opérations sensibles.
      </p>
      <p>
        En cas de violation de données personnelles susceptible d'engendrer un risque élevé pour les droits et libertés
        des personnes concernées, l'Éditeur s'engage à notifier la CNIL dans les 72 heures et, le cas échéant, à informer
        directement les Utilisateurs concernés.
      </p>
    </Article>

    <Article num={10} title="Vos droits (RGPD)">
      <p>Conformément aux articles 15 à 22 du RGPD, l'Utilisateur dispose des droits suivants :</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Droit d'accès</strong> : obtenir une copie des données personnelles le concernant ;</li>
        <li><strong>Droit de rectification</strong> : corriger les données inexactes ou incomplètes ;</li>
        <li><strong>Droit à l'effacement</strong> (« droit à l'oubli ») : demander la suppression des données ;</li>
        <li><strong>Droit à la limitation</strong> du traitement ;</li>
        <li><strong>Droit à la portabilité</strong> : recevoir les données dans un format structuré et lisible par machine ;</li>
        <li><strong>Droit d'opposition</strong> au traitement fondé sur l'intérêt légitime ;</li>
        <li><strong>Droit de définir des directives post-mortem</strong> sur le sort de ses données.</li>
      </ul>
      <p>
        Ces droits s'exercent par email à <strong>dippilote@outlook.com</strong> en justifiant de votre identité. L'Éditeur
        s'engage à répondre dans un délai maximal d'un mois.
      </p>
      <p>
        En cas de réponse insatisfaisante, l'Utilisateur peut introduire une réclamation auprès de la <strong>CNIL</strong>{' '}
        (3 place de Fontenoy, 75007 Paris — <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#205F79] underline">cnil.fr</a>).
      </p>
    </Article>

    <Article num={11} title="Cookies et traceurs">
      <p>
        Le Service utilise uniquement des cookies <strong>strictement nécessaires</strong> à son fonctionnement
        (authentification, session, sécurité). Aucun cookie publicitaire ni de profilage n'est déposé sans le consentement
        explicite de l'Utilisateur.
      </p>
    </Article>

    <Article num={12} title="Sous-traitants">
      <p>L'Éditeur fait appel aux sous-traitants suivants, qui présentent les garanties suffisantes au sens de l'article 28 RGPD :</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Stripe</strong> (paiement) — Irlande / UE.</li>
        <li>Hébergeur cloud (UE) — voir Mentions Légales.</li>
        <li>Fournisseurs d'API IA (OpenAI / Anthropic / Google) — usage encadré par leurs propres engagements de non-utilisation des données pour l'entraînement.</li>
      </ul>
    </Article>

    <Article num={13} title="Modification de la Politique de Confidentialité">
      <p>
        La présente Politique peut être modifiée à tout moment. Les Utilisateurs seront informés des modifications
        substantielles par email et/ou notification dans le Service. La date de dernière mise à jour figure en haut de
        cette page.
      </p>
    </Article>
  </LegalLayout>
);

export default Confidentialite;
